import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import * as React from 'react';

import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { PreviewRenderer_VersionFileFragment } from '@/components/PreviewRenderer/graphql-types/PreviewRenderer_VersionFileFragment';

import { VERSIONFILE_PREVIEW_FOR_AI_ASK } from '../../searchResultQueries';

const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer').then((mod) => mod.PreviewRenderer),
  {
    ssr: false,
  }
);

type VersionFilePreviewQueryData = {
  versionFilesByIdsForAiAsk: {
    results: PreviewRenderer_VersionFileFragment[];
  };
};

type VersionFilePreviewQueryVariables = {
  input: {
    versionFileIds: string[];
  };
};

export const VersionFilePreviewFromQuery: React.FC = () => {
  const router = useRouter();
  const versionFileId = React.useMemo(() => {
    if (!router.isReady) return '';
    const raw = router.query.versionFileId;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [router.isReady, router.query.versionFileId]);

  const { data } = useQuery<
    VersionFilePreviewQueryData,
    VersionFilePreviewQueryVariables
  >(VERSIONFILE_PREVIEW_FOR_AI_ASK, {
    variables: {
      input: {
        versionFileIds: [versionFileId],
      },
    },
    skip: !versionFileId,
  });

  const versionFile = data?.versionFilesByIdsForAiAsk?.results?.[0];

  const onClose = React.useCallback(() => {
    const { versionFileId: _versionFileId, ...query } = router.query;
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  }, [router]);

  if (!versionFileId || !versionFile?.file) {
    return null;
  }

  return (
    <PreviewRendererModal
      versionFile={versionFile}
      onClose={onClose}
      isOpen={router.query.versionFileId === versionFile._id}
      linkOriginalPost
    />
  );
};
